import React, { useEffect, useState, useMemo } from 'react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { copyToClipboard } from '../../lib/clipboard';
import { Trash2, Shield, Plus, UserPlus, KeyRound, X, Copy, CheckCircle2, Mail, Send, Clock, Search, Archive, ArchiveRestore } from 'lucide-react';
import { safeConfirm } from '../../lib/confirm';

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [resetLink, setResetLink] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [q, setQ] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  // Iter 140 — await safeConfirm() is silently blocked inside sandboxed iframes
  // (returns false), which made Archive/Delete look dead. In-app modal instead.
  const [confirmAction, setConfirmAction] = useState(null); // {kind, user}
  const [confirmBusy, setConfirmBusy] = useState(false);

  const load = () => api.get('/users', { params: { include_archived: showArchived } }).then(r => setUsers(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(needle) ||
      (u.email || '').toLowerCase().includes(needle) ||
      (u.role || '').toLowerCase().includes(needle)
    );
  }, [users, q]);

  const archive = (u) => {
    if (u.user_id === me.user_id) return alert('You cannot archive your own account.');
    setConfirmAction({ kind: 'archive', user: u });
  };

  const restore = async (u) => {
    try { await api.post(`/users/${u.user_id}/restore`); load(); }
    catch (e) { alert('Restore failed: ' + (e?.response?.data?.detail || e?.message)); }
  };

  const remove = (u) => {
    if (u.user_id === me.user_id) return alert('You cannot delete your own account.');
    setConfirmAction({ kind: 'delete', user: u });
  };

  const adminResetPw = (u) => setConfirmAction({ kind: 'reset', user: u });

  const runConfirmed = async () => {
    const { kind, user: u } = confirmAction;
    setConfirmBusy(true);
    try {
      if (kind === 'archive') {
        await api.post(`/users/${u.user_id}/archive`);
        load();
      } else if (kind === 'delete') {
        await api.delete(`/users/${u.user_id}`);
        load();
      } else if (kind === 'reset') {
        const { data } = await api.post('/auth/admin-reset-password', { user_id: u.user_id, send_email: true });
        setResetLink({ user: u, url: data.reset_url, expires_at: data.expires_at });
      }
      setConfirmAction(null);
    } catch (e) {
      alert(`${kind} failed: ` + (e?.response?.data?.detail || e?.message || 'unknown error'));
    } finally {
      setConfirmBusy(false);
    }
  };

  const resendInvite = async (u) => {
    try {
      const { data } = await api.post(`/clinic/users/${u.user_id}/invite/resend`);
      setInviteResult({ user: u, magic_url_preview: data.magic_url_preview, expires_at: data.expires_at });
    } catch (e) {
      alert('Resend failed: ' + (e?.response?.data?.detail || e?.message));
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">ACCESS</div>
          <h1 className="text-4xl font-heading font-bold tracking-tight">Users</h1>
          <p className="text-fg-muted mt-2">Admin, clinician, reception, patient accounts. <strong>Archive</strong> hides a user and revokes login but preserves their medical data — restorable. <strong>Delete</strong> permanently purges everything.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={()=>setShowInvite(true)} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="invite-staff-btn">
            <Mail className="w-4 h-4"/> Send magic invite
          </button>
          <button onClick={()=>setShowCreate(true)} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="new-staff-btn">
            <UserPlus className="w-4 h-4"/> Create with temp password
          </button>
        </div>
      </div>

      <div className="admin-card">
        {/* Search bar + Active/Archived toggle. */}
        <div className="flex flex-wrap items-center gap-3 mb-4 pb-3 border-b border-soft">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"/>
            <input
              type="search"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search by name, email or role…"
              className="input pl-9"
              data-testid="users-search"
            />
          </div>
          <div className="flex gap-1 rounded-md border border-soft p-0.5" data-testid="users-archive-toggle">
            <button onClick={()=>setShowArchived(false)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition ${!showArchived ? 'bg-brand text-white' : 'text-fg-muted hover:text-fg'}`}
                    data-testid="users-tab-active">Active</button>
            <button onClick={()=>setShowArchived(true)}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition inline-flex items-center gap-1 ${showArchived ? 'bg-brand text-white' : 'text-fg-muted hover:text-fg'}`}
                    data-testid="users-tab-archived">
              <Archive className="w-3.5 h-3.5"/> Archive
            </button>
          </div>
          <div className="text-xs text-fg-muted ml-auto" data-testid="users-count">{filtered.length === users.length ? `${users.length}` : `${filtered.length} of ${users.length}`}</div>
        </div>

        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Locale</th><th>Created</th><th></th></tr></thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className={u.archived ? 'opacity-60' : ''}>
                <td>{u.name}</td>
                <td className="text-xs text-fg-muted">{u.email}</td>
                <td>
                  <span className={u.role === 'admin' ? 'badge-success' : 'badge-neutral'}>
                    {u.role === 'admin' ? <Shield className="w-3 h-3 mr-1"/> : null}{u.role}
                  </span>
                  {u.profession && (
                    <span className="ml-1.5 text-[10px] text-fg-muted" data-testid={`user-profession-${u.user_id}`}>· {u.profession}</span>
                  )}
                  {u.must_change_password && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-[#FFB000] bg-[#FFF8E7] px-1.5 py-0.5 rounded uppercase tracking-wider" title="Awaiting first activation">
                      <Clock className="w-3 h-3"/> Pending
                    </span>
                  )}
                  {u.archived && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-[#7A827A] bg-[#F2EBDB] px-1.5 py-0.5 rounded uppercase tracking-wider" title="Archived — login revoked">
                      <Archive className="w-3 h-3"/> Archived
                    </span>
                  )}
                </td>
                <td className="text-xs">{u.locale}</td>
                <td className="text-xs text-fg-muted">{(u.created_at || '').slice(0,10)}</td>
                <td>
                  <div className="flex gap-3 text-sm flex-wrap">
                    {!u.archived && u.must_change_password && u.role !== 'patient' && (
                      <button onClick={()=>resendInvite(u)} className="text-[#FFB000] inline-flex items-center gap-1" data-testid={`resend-invite-${u.user_id}`} title="Resend magic invite — user hasn't activated yet">
                        <Send className="w-3.5 h-3.5"/> Resend invite
                      </button>
                    )}
                    {!u.archived && (
                      <button onClick={()=>adminResetPw(u)} className="text-brand inline-flex items-center gap-1" data-testid={`reset-pw-${u.user_id}`} title="Reset password — email link to user">
                        <KeyRound className="w-3.5 h-3.5"/> Reset PW
                      </button>
                    )}
                    {!u.archived && u.user_id !== me?.user_id && (
                      <button onClick={()=>archive(u)} className="text-fg-muted inline-flex items-center gap-1" data-testid={`archive-user-${u.user_id}`} title="Soft-delete — preserves medical data, restorable">
                        <Archive className="w-3.5 h-3.5"/> Archive
                      </button>
                    )}
                    {u.archived && (
                      <button onClick={()=>restore(u)} className="text-brand inline-flex items-center gap-1" data-testid={`restore-user-${u.user_id}`} title="Restore — reinstates login">
                        <ArchiveRestore className="w-3.5 h-3.5"/> Restore
                      </button>
                    )}
                    {u.user_id !== me?.user_id && (
                      <button onClick={()=>remove(u)} className="text-[#B83A3A] inline-flex items-center gap-1" data-testid={`delete-user-${u.user_id}`} title="Permanent delete — cascades all medical data">
                        <Trash2 className="w-3.5 h-3.5"/> Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan="6" className="text-center py-8 text-fg-muted">{q ? `No users match "${q}".` : (showArchived ? 'No archived users.' : 'No users.')}</td></tr>}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <InviteStaffModal onClose={()=>setShowInvite(false)} onSent={(info)=>{ setShowInvite(false); setInviteResult(info); load(); }}/>
      )}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={()=>setConfirmAction(null)} data-testid="confirm-user-action-modal">
          <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e=>e.stopPropagation()}>
            <div className="p-5">
              <h2 className="font-heading font-semibold mb-2">
                {confirmAction.kind === 'delete' && 'Permanently delete user?'}
                {confirmAction.kind === 'archive' && 'Archive user?'}
                {confirmAction.kind === 'reset' && 'Reset password?'}
              </h2>
              <p className="text-sm text-fg-muted">
                {confirmAction.kind === 'delete' && (
                  <>Delete <strong>{confirmAction.user.email}</strong> and cascade ALL their medical data. This cannot be undone — consider Archive instead.</>
                )}
                {confirmAction.kind === 'archive' && (
                  <>Archive <strong>{confirmAction.user.email}</strong>? They'll be hidden from the active list and lose login access. Restorable anytime.</>
                )}
                {confirmAction.kind === 'reset' && (
                  <>Generate a one-time reset link for <strong>{confirmAction.user.email}</strong> and email it to them?</>
                )}
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={()=>setConfirmAction(null)} className="btn-secondary text-sm" data-testid="confirm-user-cancel">Cancel</button>
                <button onClick={runConfirmed} disabled={confirmBusy}
                        className={`text-sm rounded-md px-4 py-2 text-white disabled:opacity-50 ${confirmAction.kind === 'delete' ? 'bg-[#B83A3A] hover:bg-[#8F2C2C]' : 'bg-brand hover:opacity-90'}`}
                        data-testid="confirm-user-confirm">
                  {confirmBusy ? 'Working…' : confirmAction.kind === 'delete' ? 'Delete permanently' : confirmAction.kind === 'archive' ? 'Archive' : 'Send reset link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {inviteResult && (
        <InviteSentModal info={inviteResult} onClose={()=>setInviteResult(null)}/>
      )}
      {showCreate && (
        <CreateStaffModal onClose={()=>setShowCreate(false)} onCreated={(info) => { setCreatedInfo(info); setShowCreate(false); load(); }}/>
      )}
      {createdInfo && (
        <CredentialsModal info={createdInfo} onClose={()=>setCreatedInfo(null)} title="Temporary credentials" hint="Copy and hand them to the new staff member. They'll be prompted to change the password on first login."/>
      )}
      {resetLink && (
        <CredentialsModal info={{ email: resetLink.user.email, temporary_password: null, url: resetLink.url, expires_at: resetLink.expires_at }} onClose={()=>setResetLink(null)} title="Password reset link" hint="Email sent to user. You may also share this link directly — it expires in 60 minutes."/>
      )}
    </div>
  );
}

function professionOptions(services) {
  const set = new Set();
  services.forEach(s => {
    const v = (s.discipline || s.name || '').trim();
    if (v) set.add(v.charAt(0).toUpperCase() + v.slice(1));
  });
  return [...set];
}

function InviteStaffModal({ onClose, onSent }) {
  const [form, setForm] = useState({ name: '', email: '', role: 'reception', locale: 'en', profession: '' });
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/services').then(r => setServices((r.data || []).filter(s => s.active !== false))).catch(()=>{});
  }, []);

  const send = async () => {
    setSubmitting(true); setErr('');
    try {
      const { data } = await api.post('/clinic/users/invite/magic',
        { ...form, profession: form.role === 'clinician' ? form.profession || null : null });
      onSent({ ...data, name: form.name, role: form.role });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to send invite');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="invite-staff-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-soft">
          <h2 className="font-heading font-semibold inline-flex items-center gap-2"><Mail className="w-4 h-4 text-brand"/> Magic invite</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-xs text-fg-muted -mt-1">
            We'll email a one-click sign-in link. They'll pick their own password on first visit — no temp credentials to share.
          </p>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Full name</div>
            <input className="input" value={form.name} onChange={e=>setForm(f=>({...f, name: e.target.value}))} data-testid="invite-name"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Email</div>
            <input className="input" type="email" value={form.email} onChange={e=>setForm(f=>({...f, email: e.target.value}))} data-testid="invite-email"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Role</div>
            <select className="input" value={form.role} onChange={e=>setForm(f=>({...f, role: e.target.value}))} data-testid="invite-role">
              <option value="reception">Reception</option>
              <option value="clinician">Clinician (practitioner)</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          {form.role === 'clinician' && (
            <label className="block">
              <div className="text-xs text-fg-muted mb-1">Professional service</div>
              <select className="input" value={form.profession} onChange={e=>setForm(f=>({...f, profession: e.target.value}))} data-testid="invite-profession">
                <option value="">— General practitioner —</option>
                {professionOptions(services).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="text-[10px] text-fg-muted mt-1">Pulled from your clinic's professional services catalogue.</div>
            </label>
          )}
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Locale</div>
            <select className="input" value={form.locale} onChange={e=>setForm(f=>({...f, locale: e.target.value}))}>
              <option value="en">English</option>
              <option value="pl">Polish</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
            </select>
          </label>
          {err && <div className="text-sm text-[#B83A3A] flex items-center gap-1.5"><span className="w-4 h-4 rounded-full bg-[#B83A3A]/15 text-[#B83A3A] inline-flex items-center justify-center text-[10px]">!</span>{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={send} disabled={submitting || !form.name || !form.email} className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="invite-send">
              <Send className="w-3.5 h-3.5"/> {submitting ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InviteSentModal({ info, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const ok = await copyToClipboard(info.magic_url_preview, { silent: true });
    if (ok) { setCopied(true); setTimeout(()=>setCopied(false), 2000); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="invite-sent-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-soft">
          <h2 className="font-heading font-semibold inline-flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#1B5E20]"/> Invite sent</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5">
          <p className="text-sm">A magic sign-in link was emailed to <strong>{info.email || info.user?.email}</strong>. They have 60 minutes to accept.</p>

          {/* Direct-link fallback visible above the fold (was buried inside <details>).
              If the email never arrives the admin can just hit Copy here. */}
          <div className="mt-4">
            <div className="text-xs text-fg-muted mb-1.5">Didn't get the email? Share this direct link:</div>
            <div className="surface border border-soft rounded p-2 font-mono break-all text-[11px] mb-2" data-testid="invite-direct-link">
              {info.magic_url_preview}
            </div>
            <button onClick={copy} className="btn-secondary inline-flex items-center gap-1.5 text-xs" data-testid="invite-copy">
              {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-[#1B5E20]"/> : <Copy className="w-3.5 h-3.5"/>}
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>

          <div className="mt-5 flex justify-end">
            <button onClick={onClose} className="btn-primary text-sm">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateStaffModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', role: 'reception', locale: 'en', profession: '' });
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/services').then(r => setServices((r.data || []).filter(s => s.active !== false))).catch(()=>{});
  }, []);

  const save = async () => {
    setSubmitting(true); setErr('');
    try {
      const { data } = await api.post('/admin/users/create',
        { ...form, profession: form.role === 'clinician' ? form.profession || null : null });
      onCreated(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to create user');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="create-staff-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-soft">
          <h2 className="font-heading font-semibold">New staff user</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Full name</div>
            <input className="input" value={form.name} onChange={e=>setForm(f=>({...f, name: e.target.value}))} data-testid="staff-name"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Email</div>
            <input className="input" type="email" value={form.email} onChange={e=>setForm(f=>({...f, email: e.target.value}))} data-testid="staff-email"/>
          </label>
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Role</div>
            <select className="input" value={form.role} onChange={e=>setForm(f=>({...f, role: e.target.value}))} data-testid="staff-role">
              <option value="reception">Reception</option>
              <option value="clinician">Clinician (practitioner)</option>
              <option value="admin">Admin</option>
            </select>
          </label>
          {form.role === 'clinician' && (
            <label className="block">
              <div className="text-xs text-fg-muted mb-1">Professional service</div>
              <select className="input" value={form.profession} onChange={e=>setForm(f=>({...f, profession: e.target.value}))} data-testid="staff-profession">
                <option value="">— General practitioner —</option>
                {professionOptions(services).map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="text-[10px] text-fg-muted mt-1">Pulled from your clinic's professional services catalogue.</div>
            </label>
          )}
          <label className="block">
            <div className="text-xs text-fg-muted mb-1">Locale</div>
            <select className="input" value={form.locale} onChange={e=>setForm(f=>({...f, locale: e.target.value}))}>
              <option value="en">English</option>
              <option value="pl">Polish</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
            </select>
          </label>
          {err && <div className="text-sm text-[#B83A3A]">{err}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
            <button onClick={save} disabled={submitting || !form.name || !form.email} className="btn-primary text-sm" data-testid="staff-save">
              {submitting ? 'Creating…' : 'Create user'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CredentialsModal({ info, onClose, title, hint }) {
  const [copied, setCopied] = useState(false);
  const text = info.url
    ? `Reset link for ${info.email}:\n${info.url}\n(expires ${info.expires_at})`
    : `Email: ${info.email}\nTemporary password: ${info.temporary_password}`;
  const copy = async () => {
    const ok = await copyToClipboard(text, { silent: true });
    if (ok) { setCopied(true); setTimeout(()=>setCopied(false), 2000); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="credentials-modal">
      <div className="surface border border-soft rounded-xl shadow-xl w-full max-w-md" onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-soft">
          <h2 className="font-heading font-semibold">{title}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5">
          <p className="text-xs text-fg-muted mb-3">{hint}</p>
          <pre className="bg-[var(--bg-tertiary)] border border-soft rounded p-3 text-xs font-mono whitespace-pre-wrap break-all">{text}</pre>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={copy} className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="copy-creds">
              {copied ? <CheckCircle2 className="w-4 h-4 text-brand"/> : <Copy className="w-4 h-4"/>}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button onClick={onClose} className="btn-primary text-sm">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}
